import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { formatPhone } from '../lib/phone';
import { apiFetch, simulatePaymeTopup } from '../api';
import {
  PageHeader, Loading, ErrorBanner, Empty, Badge, Modal, ConfirmDialog, Toast, fmtDate, asList, ui,
} from '../components/adminUi';
import Icon from '../components/Icon';

// Ustalarning kredit hisobi: to'ldirish so'rovlarini tasdiqlash + balanslarni ko'rish
// va qo'lda tuzatish. Har tuzatish daftarga (credit_transactions) yozuv qo'yadi.
export default function CreditsPage() {
  const { auth } = useAuth();

  const [status, setStatus] = useState('pending');
  const [provider, setProvider] = useState(''); // '' = hammasi | 'payme' | 'manual'
  const [topups, setTopups] = useState([]);
  const [balances, setBalances] = useState([]);
  // Kutayotganlar soni backenddan (filtrlardan mustaqil): qo'lda va jami (qo'lda + Payme).
  const [pendingCounts, setPendingCounts] = useState(null);
  const [fees, setFees] = useState([]);
  const [feeTotals, setFeeTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [rowMsg, setRowMsg] = useState({});

  // 🧪 Test muhit: Payme simulyatsiyasi. Backend `testPayAvailable` ni faqat test muhitda
  // true qaytaradi; simulyatsiya 404 bersa (prod) tugmalar sessiya davomida yashiriladi.
  const [testPayAvailable, setTestPayAvailable] = useState(false);
  const [simHidden, setSimHidden] = useState(false);
  const [simBusyId, setSimBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  // Qo'lda tuzatish modali
  const [adjustFor, setAdjustFor] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [adjustError, setAdjustError] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  // Prime (Faza 5) — qo'lda berish/bekor qilish
  const [primeFor, setPrimeFor] = useState(null);
  const [primeDays, setPrimeDays] = useState('30');
  const [primeError, setPrimeError] = useState('');
  const [primeBusy, setPrimeBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const q = new URLSearchParams({ status, withCounts: '1' });
      if (provider) q.set('provider', provider);
      const [tRes, bRes, fRes] = await Promise.all([
        apiFetch(`/api/admin/credits/topups/list?${q}`, auth.token),
        apiFetch('/api/admin/credits', auth.token),
        apiFetch('/api/admin/credits/deal-fees', auth.token),
      ]);
      if (!tRes.ok || !bRes.ok) throw new Error();
      const t = await tRes.json();
      const b = await bRes.json();
      // Manba filtri mijozda ham qo'llanadi: `provider` ni hali tanimaydigan backend
      // hamma qatorni qaytarsa ham ro'yxat tanlangan manbaga mos bo'lsin.
      const rows = asList(t).map(normalizeTopup);
      const manualN = t && (t.manualPendingCount ?? t.manual_pending_count);
      const totalN = t && (t.pendingCount ?? t.pending_count);
      setPendingCounts(typeof manualN === 'number'
        ? { manual: manualN, total: typeof totalN === 'number' ? totalN : manualN }
        : null);
      setTopups(provider ? rows.filter((r) => r.provider === provider) : rows);
      setTestPayAvailable(!!t && (t.testPayAvailable ?? t.test_pay_available) === true);
      setBalances(Array.isArray(b) ? b : []);
      // Kelishuv komissiyalari (deal_fees) — yiqilsa asosiy sahifa buzilmasin.
      if (fRes.ok) {
        const f = await fRes.json();
        setFees(Array.isArray(f.items) ? f.items : []);
        setFeeTotals(f.totals || null);
      } else {
        setFees([]); setFeeTotals(null);
      }
    } catch {
      setTopups([]);
      setBalances([]);
      setFetchError("Ma'lumotni yuklashda xatolik. Sahifani yangilang.");
    } finally {
      setLoading(false);
    }
  }, [auth.token, status, provider]);

  useEffect(() => { load(); }, [load]);

  const decide = async (row, action) => {
    // Payme so'rovlarini backend 409 PAYME_MANAGED bilan qaytaradi — umuman chaqirmaymiz.
    if (!canModerate(row)) return;
    setBusyId(row.id);
    setRowMsg((m) => ({ ...m, [row.id]: null }));
    try {
      const res = await apiFetch(`/api/admin/credits/topups/${row.id}/${action}`, auth.token, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setRowMsg((m) => ({
          ...m,
          [row.id]: {
            tone: 'green',
            text: action === 'approve' ? `Tasdiqlandi — yangi balans ${money(d.balance)} so'm` : 'Rad etildi',
          },
        }));
        load();
      } else if (res.status === 409 && d.code === 'PAYME_MANAGED') {
        setRowMsg((m) => ({
          ...m,
          [row.id]: { tone: 'red', text: "Bu to'lov Payme tomonidan boshqariladi, qo'lda tasdiqlab bo'lmaydi" },
        }));
        load();
      } else {
        setRowMsg((m) => ({ ...m, [row.id]: { tone: 'red', text: d.message || 'Xatolik' } }));
      }
    } catch {
      setRowMsg((m) => ({ ...m, [row.id]: { tone: 'red', text: 'Tarmoq xatosi' } }));
    } finally {
      setBusyId(null);
    }
  };

  // Tasdiq oynasi (sahifa ichida): { kind: 'reject' | 'sim', row, scenario? }
  const [confirmFor, setConfirmFor] = useState(null);

  const askSimulate = (row, scenario) => {
    if (simBusyId != null || !canSimulate(row)) return;
    setConfirmFor({ kind: 'sim', row, scenario });
  };

  const onConfirmed = async () => {
    const c = confirmFor;
    if (!c) return;
    if (c.kind === 'reject') {
      setConfirmFor(null);
      await decide(c.row, 'reject');
      return;
    }
    await simulate(c.row, c.scenario);
    setConfirmFor(null);
  };

  const simulate = async (row, scenario) => {
    if (simBusyId != null || !canSimulate(row)) return;
    setSimBusyId(row.id);
    const { ok, status: httpStatus, data: d } = await simulatePaymeTopup(row.id, scenario, auth.token);
    setSimBusyId(null);
    if (httpStatus === 404) {
      // Prod himoyasi: endpoint yo'q — tugmalar sahifa sessiyasi davomida yashiriladi.
      setSimHidden(true);
      setToast({ type: 'error', text: "Simulyatsiya bu muhitda mavjud emas" });
      return;
    }
    if (ok) {
      const before = d.balanceBefore ?? d.balance_before;
      const after = d.balanceAfter ?? d.balance_after;
      const bal = before != null && after != null ? ` Balans: ${money(before)} → ${money(after)} so'm` : '';
      const text = (d.alreadyProcessed ?? d.already_processed)
        ? "Bu so'rov allaqachon qayta ishlangan."
        : scenario === 'pay' ? `To'lov simulyatsiya qilindi.${bal}` : 'Bekor qilish simulyatsiya qilindi.';
      setToast({ type: 'success', text });
      load();
    } else {
      setToast({
        type: 'error',
        text: httpStatus === 0 ? "Tarmoq xatosi. Qayta urinib ko'ring." : (d.message || 'Simulyatsiyada xatolik yuz berdi.'),
      });
      if (httpStatus === 409) load();
    }
  };

  const showSim = testPayAvailable && !simHidden;

  const openAdjust = (u) => {
    setAdjustFor(u);
    setAdjustAmount('');
    setAdjustNote('');
    setAdjustError('');
  };

  const submitAdjust = async () => {
    const amt = Number(String(adjustAmount).replace(/\s/g, ''));
    if (!Number.isFinite(amt) || amt === 0) {
      setAdjustError("Miqdorni kiriting (qo'shish uchun musbat, yechish uchun manfiy)");
      return;
    }
    setAdjusting(true);
    setAdjustError('');
    try {
      const res = await apiFetch(`/api/admin/credits/${adjustFor.id}/adjust`, auth.token, {
        method: 'POST',
        body: JSON.stringify({ amount: amt, note: adjustNote.trim() || undefined }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setAdjustFor(null);
        load();
      } else {
        setAdjustError(d.message || 'Xatolik');
      }
    } catch {
      setAdjustError('Tarmoq xatosi');
    } finally {
      setAdjusting(false);
    }
  };

  const submitPrime = async (days) => {
    const n = Number(days);
    if (!Number.isFinite(n)) { setPrimeError('Kunlar soni noto\'g\'ri'); return; }
    setPrimeBusy(true);
    setPrimeError('');
    try {
      const res = await apiFetch(`/api/admin/credits/${primeFor.id}/prime`, auth.token, {
        method: 'POST',
        body: JSON.stringify({ days: n }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { setPrimeFor(null); load(); }
      else setPrimeError(d.message || 'Xatolik');
    } catch {
      setPrimeError('Tarmoq xatosi');
    } finally {
      setPrimeBusy(false);
    }
  };

  // Nishon: faqat admin QO'LDA tasdiqlay oladigan kutayotganlar (Payme'nikini Payme o'zi yopadi).
  // Manba: backend `manualPendingCount`; eski backend bo'lsa — joriy ro'yxatdan hisoblanadi.
  const pendingCount = pendingCounts ? pendingCounts.manual : topups.filter(canModerate).length;
  const paymePending = pendingCounts ? Math.max(0, pendingCounts.total - pendingCounts.manual) : null;

  return (
    <AdminLayout>
      <PageHeader
        title="Kredit hisoblari"
        subtitle="Ustalarning to'ldirish so'rovlarini tasdiqlang va balanslarini boshqaring"
      >
        <select style={{ ...ui.select, ...ui.headerSelect }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="pending">Kutilayotgan so'rovlar</option>
          <option value="approved">Tasdiqlangan</option>
          <option value="rejected">Rad etilgan</option>
          <option value="all">Hammasi</option>
        </select>
        <select
          style={{ ...ui.select, ...ui.headerSelect }}
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          aria-label="Manba"
          title="Manba"
        >
          <option value="">Manba: Hammasi</option>
          <option value="payme">Manba: Payme (avtomatik)</option>
          <option value="manual">Manba: Qo'lda</option>
        </select>
        <button style={ui.pagerBtn} onClick={load}><Icon name="refresh" size={14} style={{ marginRight: '6px' }} />Yangilash</button>
      </PageHeader>

      <ErrorBanner>{fetchError}</ErrorBanner>

      {loading ? (
        <Loading />
      ) : (
        <>
          <h2 style={s.section}>
            To'ldirish so'rovlari
            {pendingCount > 0 && (
              <>
                <span
                  style={s.count}
                  title={paymePending != null
                    ? `Jami kutayotgan: ${pendingCounts.total} (qo'lda: ${pendingCounts.manual}, Payme: ${paymePending} — avtomatik)`
                    : undefined}
                >
                  {pendingCount}
                </span>
                <span style={s.countHint}>{`${pendingCount} ta qo'lda tasdiq kutmoqda`}</span>
              </>
            )}
          </h2>

          {topups.length === 0 ? (
            <Empty
              icon="creditCard"
              title="Hali to'ldirish yo'q"
              hint={emptyHint(status, provider)}
            />
          ) : (
            <div style={ui.tableWrap}>
              <table style={ui.table}>
                <thead>
                  <tr>
                    <th style={ui.th}>Usta</th>
                    <th style={{ ...ui.th, width: '110px' }}>Manba</th>
                    <th style={{ ...ui.th, width: '130px' }}>Summa</th>
                    <th style={{ ...ui.th, width: '140px' }}>Yaratilgan</th>
                    <th style={ui.th}>Holat</th>
                    <th style={{ ...ui.th, width: '215px', textAlign: 'right' }}>Amal</th>
                  </tr>
                </thead>
                <tbody>
                  {topups.map((row) => {
                    const msg = rowMsg[row.id];
                    const busy = busyId === row.id;
                    const isPayme = row.provider === 'payme';
                    return (
                      <tr key={row.id} style={ui.tr}>
                        <td style={ui.td}>
                          <div style={{ fontWeight: '600', color: 'var(--color-text)' }}>{row.ustaName}</div>
                          <div style={s.sub}>{formatPhone(row.ustaPhone) || '—'}</div>
                        </td>
                        <td style={ui.td}>
                          {isPayme ? <Badge tone="blue"><Icon name="creditCard" size={12} style={{ marginRight: '6px' }} />Payme (avtomatik)</Badge> : <Badge tone="gray"><Icon name="edit" size={12} style={{ marginRight: '6px' }} />Qo'lda</Badge>}
                          {!isPayme && row.method && <div style={s.sub}>{row.method}</div>}
                        </td>
                        <td style={{ ...ui.td, fontWeight: '700', color: 'var(--color-text)' }}>
                          {money(row.amount)} so'm
                        </td>
                        <td style={ui.td}>{fmtDate(row.createdAt, true)}</td>
                        <td style={ui.td}>
                          {isPayme ? <PaymeStatus row={row} /> : <StatusBadge status={row.status} />}
                        </td>
                        <td style={{ ...ui.td, textAlign: 'right' }}>
                          {canModerate(row) ? (
                            <div style={ui.actions}>
                              <button
                                style={{ ...ui.btnSm, ...ui.btnGreen, ...(busy ? ui.btnDisabled : {}) }}
                                onClick={() => decide(row, 'approve')}
                                disabled={busy}
                              >
                                {busy ? '...' : (<><Icon name="check" size={14} style={{ marginRight: '6px' }} />Tasdiqlash</>)}
                              </button>
                              <button
                                style={{ ...ui.btnSm, ...ui.btnRed, ...(busy ? ui.btnDisabled : {}) }}
                                onClick={() => setConfirmFor({ kind: 'reject', row })}
                                disabled={busy}
                              >
                                <Icon name="x" size={14} style={{ marginRight: '6px' }} />Rad etish
                              </button>
                            </div>
                          ) : isPayme ? (
                            <Badge tone="blue">Payme (avtomatik)</Badge>
                          ) : (
                            <span style={s.decided}>{fmtDate(row.decidedAt, true) || '—'}</span>
                          )}
                          {showSim && canSimulate(row) && (
                            <div style={s.simRow}>
                              <button
                                style={{ ...ui.btnSm, ...ui.btnGray, ...(simBusyId != null ? ui.btnDisabled : {}) }}
                                onClick={() => askSimulate(row, 'pay')}
                                disabled={simBusyId != null}
                              >
                                {simBusyId === row.id ? '...' : "🧪 Simulyatsiya: to'lash"}
                              </button>
                              <button
                                style={{ ...ui.btnSm, ...ui.btnGray, ...(simBusyId != null ? ui.btnDisabled : {}) }}
                                onClick={() => askSimulate(row, 'cancel_before')}
                                disabled={simBusyId != null}
                              >
                                {simBusyId === row.id ? '...' : '🧪 Simulyatsiya: bekor'}
                              </button>
                            </div>
                          )}
                          {msg && <div style={{ ...s.msg, ...toneStyle(msg.tone) }}>{msg.text}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {feeTotals && (feeTotals.charged.n > 0 || feeTotals.pending.n > 0 || feeTotals.waived.n > 0) && (
            <>
              <h2 style={{ ...s.section, marginTop: '24px' }}>Kelishuv komissiyalari</h2>
              <div style={s.totals}>
                <Totals label="Olingan" v={feeTotals.charged} />
                <Totals label="Kutilmoqda" v={feeTotals.pending} />
                <Totals label="Prime (olinmadi)" v={feeTotals.waived} />
              </div>
              <div style={ui.tableWrap}>
                <table style={ui.table}>
                  <thead>
                    <tr>
                      <th style={{ ...ui.th, width: '90px' }}>E'lon</th>
                      <th style={ui.th}>Usta</th>
                      <th style={ui.th}>Mijoz</th>
                      <th style={{ ...ui.th, width: '130px' }}>Miqdor</th>
                      <th style={{ ...ui.th, width: '110px' }}>To'lovchi</th>
                      <th style={{ ...ui.th, width: '130px' }}>Holat</th>
                      <th style={{ ...ui.th, width: '140px' }}>Sana</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fees.map((f) => (
                      <tr key={f.id} style={ui.tr}>
                        <td style={ui.td}>#{f.taskId}</td>
                        <td style={{ ...ui.td, color: 'var(--color-text)' }}>
                          {[f.providerFirstName, f.providerLastName].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td style={ui.td}>
                          {[f.clientFirstName, f.clientLastName].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td style={{ ...ui.td, fontWeight: '700', color: 'var(--color-text)' }}>
                          {money(f.amount)} so'm
                        </td>
                        <td style={ui.td}>{f.payer === 'client' ? 'Mijoz' : 'Usta'}</td>
                        <td style={ui.td}><FeeBadge status={f.status} reason={f.reason} /></td>
                        <td style={ui.td}>{fmtDate(f.createdAt, true)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <h2 style={{ ...s.section, marginTop: '24px' }}>Ustalar balansi</h2>
          <p style={s.hint}>
            Taklif yuborish bepul — ulush mijoz taklifni <b>qabul qilganda</b> (kelishuv paytida)
            olinadi. Balansi ulushga yetmagan usta yangi taklif yubora olmaydi. Qo'lda tuzatish
            daftarga yozuv qo'yadi.
          </p>

          {balances.length === 0 ? (
            <Empty icon="wrench" title="Usta yo'q" hint="Hali ro'yxatdan o'tgan usta yo'q." />
          ) : (
            <div style={ui.tableWrap}>
              <table style={ui.table}>
                <thead>
                  <tr>
                    <th style={ui.th}>Usta</th>
                    <th style={ui.th}>Telefon</th>
                    <th style={{ ...ui.th, width: '170px' }}>Balans</th>
                    <th style={{ ...ui.th, width: '150px' }}>Prime</th>
                    <th style={{ ...ui.th, width: '160px' }}>Oxirgi o'zgarish</th>
                    <th style={{ ...ui.th, width: '210px', textAlign: 'right' }}>Amal</th>
                  </tr>
                </thead>
                <tbody>
                  {balances.map((u) => (
                    <tr key={u.id} style={ui.tr}>
                      <td style={{ ...ui.td, fontWeight: '600', color: 'var(--color-text)' }}>{fullName(u)}</td>
                      <td style={ui.td}>{formatPhone(u.phone) || '—'}</td>
                      <td style={ui.td}>
                        <span style={{ ...s.balance, ...(u.balance <= 0 ? s.balanceLow : {}) }}>
                          {money(u.balance)} so'm
                        </span>
                        {u.balance <= 0 && <Badge tone="red">taklif yubora olmaydi</Badge>}
                      </td>
                      <td style={ui.td}><PrimeCell until={u.primeUntil} /></td>
                      <td style={ui.td}>{fmtDate(u.updatedAt, true) || '—'}</td>
                      <td style={{ ...ui.td, textAlign: 'right' }}>
                        <div style={ui.actions}>
                          <button style={{ ...ui.btnSm, ...ui.btnBlue }} onClick={() => openAdjust(u)}>
                            ± Tuzatish
                          </button>
                          <button
                            style={{ ...ui.btnSm, ...ui.btnGray }}
                            onClick={() => { setPrimeFor(u); setPrimeDays('30'); setPrimeError(''); }}>
                            <Icon name="star" size={12} style={{ marginRight: '6px' }} />Prime
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {adjustFor && (
        <Modal title={`${fullName(adjustFor)} — balansni tuzatish`} onClose={() => setAdjustFor(null)}>
          <p style={s.modalHint}>
            Joriy balans: <b>{money(adjustFor.balance)} so'm</b>. Qo'shish uchun musbat, yechish
            uchun manfiy miqdor kiriting.
          </p>
          <div style={ui.field}>
            <label style={ui.label}>Miqdor (so'm) <span style={ui.req}>*</span></label>
            <input
              style={ui.input}
              value={adjustAmount}
              onChange={(e) => setAdjustAmount(e.target.value.replace(/[^0-9-]/g, ''))}
              placeholder="masalan: 100000 yoki -50000"
              autoFocus
            />
          </div>
          <div style={ui.field}>
            <label style={ui.label}>Izoh</label>
            <input
              style={ui.input}
              value={adjustNote}
              onChange={(e) => setAdjustNote(e.target.value)}
              placeholder="nima uchun tuzatilyapti"
            />
          </div>
          {adjustError && <div style={ui.formError}>{adjustError}</div>}
          <div style={ui.modalActions}>
            <button style={{ ...ui.btnSm, ...ui.btnGray }} onClick={() => setAdjustFor(null)} disabled={adjusting}>
              Bekor qilish
            </button>
            <button
              style={{ ...ui.saveBtn, ...(adjusting ? ui.btnDisabled : {}) }}
              onClick={submitAdjust}
              disabled={adjusting}
            >
              {adjusting ? '...' : 'Saqlash'}
            </button>
          </div>
        </Modal>
      )}

      {primeFor && (
        <Modal title={`${fullName(primeFor)} — Prime`} onClose={() => setPrimeFor(null)}>
          <p style={s.modalHint}>
            Joriy holat: <b>{primeFor.primeUntil && new Date(primeFor.primeUntil) > new Date()
              ? `faol, ${fmtDate(primeFor.primeUntil)} gacha` : 'faol emas'}</b>.
            Qo'lda berish <b>pul yechmaydi</b> — naqd/tashqi to'lov uchun. Usta o'zi
            balansidan ham sotib olishi mumkin.
          </p>
          <div style={ui.field}>
            <label style={ui.label}>Necha kun qo'shilsin</label>
            <input
              style={ui.input}
              value={primeDays}
              onChange={(e) => setPrimeDays(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="30"
            />
          </div>
          {primeError && <div style={ui.formError}>{primeError}</div>}
          <div style={ui.modalActions}>
            <button
              style={{ ...ui.btnSm, ...ui.btnRed }}
              onClick={() => submitPrime(0)}
              disabled={primeBusy}>
              Bekor qilish (Prime'ni o'chirish)
            </button>
            <button
              style={{ ...ui.saveBtn, ...(primeBusy ? ui.btnDisabled : {}) }}
              onClick={() => submitPrime(primeDays)}
              disabled={primeBusy}>
              {primeBusy ? '...' : 'Qo\'shish'}
            </button>
          </div>
        </Modal>
      )}

      {confirmFor && (
        <ConfirmDialog
          title={confirmFor.kind === 'reject' ? "So'rovni rad etish" : '🧪 Simulyatsiya'}
          message={confirmText(confirmFor)}
          confirmLabel="Tasdiqlash"
          busyLabel="Yuborilmoqda…"
          danger={confirmFor.kind === 'reject' || confirmFor.scenario === 'cancel_before'}
          busy={confirmFor.kind === 'sim' && simBusyId != null}
          onCancel={() => setConfirmFor(null)}
          onConfirm={onConfirmed}
        />
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </AdminLayout>
  );
}

function confirmText({ kind, row, scenario }) {
  const who = `#${row.id} · ${row.ustaName} — ${money(row.amount)} so'm`;
  if (kind === 'reject') return `${who} so'rovi rad etilsinmi?`;
  return scenario === 'pay'
    ? `${who} to'lovi "to'landi" qilinsinmi?`
    : `${who} to'lovi bekor qilinsinmi?`;
}

function fullName(r) {
  return [r.firstName, r.lastName].filter(Boolean).join(' ') || `#${r.userId || r.id}`;
}

// Bo'sh joyli format: 100 000 (manfiyda minus oldinda qoladi)
function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  const sign = v < 0 ? '−' : '';
  return sign + Math.round(Math.abs(v)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function StatusBadge({ status }) {
  if (status === 'approved') return <Badge tone="green">Tasdiqlangan</Badge>;
  if (status === 'rejected') return <Badge tone="red">Rad etilgan</Badge>;
  return <Badge tone="amber">Kutilmoqda</Badge>;
}

// ── Payme to'ldirishlari ──────────────────────────────────────────────────────

const PAYME_STATUSES = ['pending', 'paid', 'cancelled', 'refunded'];

// Payme tranzaksiya holati (state) → bizning status.
const PAYME_STATE_STATUS = { 1: 'pending', 2: 'paid', '-1': 'cancelled', '-2': 'refunded' };

// Payme CancelTransaction `reason` kodlari.
const PAYME_CANCEL_REASONS = {
  1: 'Qabul qiluvchi topilmadi',
  2: 'Debet operatsiyasida xato',
  3: "To'lovdan oldin bekor qilindi",
  4: 'Vaqt tugadi (timeout)',
  5: 'Qaytarildi',
  10: "Noma'lum xato",
};

// Backend javobini bir xil ko'rinishga keltiradi. Yangi maydonlar (provider, status,
// rawStatus, canModerate, payme*, usta*) bo'lmasa — eski maydonlardan tiklanadi.
function normalizeTopup(r) {
  const provider = r.provider || (r.method === 'payme' ? 'payme' : 'manual');
  const rawStatus = r.rawStatus || r.status;
  const paymeState = r.paymeState == null || r.paymeState === '' ? null : Number(r.paymeState);
  let status = r.status;
  if (provider === 'payme' && !PAYME_STATUSES.includes(status)) {
    status = PAYME_STATE_STATUS[paymeState]
      || (rawStatus === 'approved' ? 'paid' : rawStatus === 'rejected' ? 'cancelled' : 'pending');
  }
  return {
    ...r,
    provider,
    status,
    rawStatus,
    paymeState,
    ustaName: r.ustaName || fullName(r),
    ustaPhone: r.ustaPhone || r.phone || '',
  };
}

// Tasdiqlash/Rad etish faqat qo'lda kelgan, hali kutilayotgan so'rovlarda.
// Payme so'rovini backend 409 PAYME_MANAGED bilan qaytaradi.
function canModerate(row) {
  if (row.canModerate === false || row.provider === 'payme') return false;
  return (row.rawStatus || row.status) === 'pending';
}

// 🧪 Simulyatsiya faqat kutilayotgan Payme to'ldirishlarida.
function canSimulate(row) {
  return row.provider === 'payme' && row.status === 'pending';
}

function emptyHint(status, provider) {
  const src = provider === 'payme' ? ' (Payme)' : provider === 'manual' ? " (qo'lda)" : '';
  if (status === 'pending') return `Kutilayotgan to'ldirish so'rovi yo'q${src}.`;
  if (status === 'all') return `To'ldirishlar hali bo'lmagan${src}.`;
  return `Bu holatda to'ldirish topilmadi${src}.`;
}

function PaymeStatusBadge({ status }) {
  if (status === 'paid') return <Badge tone="green">Muvaffaqiyatli</Badge>;
  if (status === 'cancelled') return <Badge tone="red">Bekor qilindi</Badge>;
  if (status === 'refunded') return <Badge tone="purple">Qaytarildi</Badge>;
  return <Badge tone="amber">Kutilmoqda</Badge>;
}

function PaymeStatus({ row }) {
  const reasonCode = row.cancelReason == null || row.cancelReason === '' ? null : Number(row.cancelReason);
  const reason = reasonCode == null ? '' : (PAYME_CANCEL_REASONS[reasonCode] || `sabab ${reasonCode}`);
  const cancelled = row.status === 'cancelled' || row.status === 'refunded';
  return (
    <div>
      <PaymeStatusBadge status={row.status} />
      <div style={s.payme}>
        {row.performTime && <div>To'landi: <b>{fmtPaymeTime(row.performTime)}</b></div>}
        {row.cancelTime && (
          <div>
            Bekor: <b>{fmtPaymeTime(row.cancelTime)}</b>
            {reason && <> — {reason}</>}
          </div>
        )}
        {!row.cancelTime && cancelled && reason && <div>Sabab: {reason}</div>}
        {!row.cancelTime && cancelled && !reason && row.note && <div>{row.note}</div>}
        <div style={s.txId}>
          Payme ID: {row.paymeTransactionId ? <code>{row.paymeTransactionId}</code> : '—'}
        </div>
      </div>
    </div>
  );
}

// ISO satr yoki Payme ms epoch — umumiy formatlovchi orqali (Asia/Tashkent).
function fmtPaymeTime(v) {
  return fmtDate(v, true);
}

function PrimeCell({ until }) {
  if (!until || new Date(until) <= new Date()) {
    return <span style={{ color: 'var(--color-text-muted)', fontSize: '13px' }}>—</span>;
  }
  const days = Math.ceil((new Date(until) - Date.now()) / 86400000);
  return <Badge tone="amber">{<><Icon name="star" size={12} style={{ marginRight: '6px' }} />{`${days} kun`}</>}</Badge>;
}

function FeeBadge({ status, reason }) {
  if (status === 'charged') return <Badge tone="green">Olindi</Badge>;
  if (status === 'waived') return <Badge tone="gray">{reason === 'prime' ? 'Prime' : 'Olinmadi'}</Badge>;
  return <Badge tone="amber">Kutilmoqda</Badge>;
}

function Totals({ label, v }) {
  return (
    <div style={s.totalCard}>
      <div style={s.totalLabel}>{label}</div>
      <div style={s.totalValue}>{money(v.sum)} so'm</div>
      <div style={s.totalCount}>{v.n} ta</div>
    </div>
  );
}

function toneStyle(tone) {
  if (tone === 'green') return { color: 'var(--color-success)' };
  if (tone === 'red') return { color: 'var(--color-danger)' };
  return { color: 'var(--color-text-muted)' };
}

const s = {
  section: {
    margin: '8px 0 12px', fontSize: '18px', fontWeight: '700', color: 'var(--color-text)',
    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
  },
  count: {
    background: 'var(--color-primary)', color: 'var(--color-on-primary)', borderRadius: '999px',
    padding: '2px 9px', fontSize: '12px', fontWeight: '700',
  },
  countHint: { fontSize: '13px', fontWeight: '500', color: 'var(--color-text-muted)' },
  hint: { margin: '0 0 14px', fontSize: '13px', color: 'var(--color-text-muted)', maxWidth: '760px' },
  simRow: { display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: '8px' },
  msg: { fontSize: '12px', fontWeight: '600', marginTop: '6px' },
  decided: { fontSize: '13px', color: 'var(--color-text-muted)' },
  sub: { fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '3px' },
  payme: { fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '6px', lineHeight: 1.5 },
  txId: { color: 'var(--color-text-muted)', wordBreak: 'break-all' },
  balance: { fontWeight: '700', color: 'var(--color-text)', marginRight: '8px' },
  balanceLow: { color: 'var(--color-danger)' },
  modalHint: { margin: '0 0 16px', fontSize: '13px', color: 'var(--color-text-muted)' },
  totals: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' },
  totalCard: {
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-card)', padding: '16px',
  },
  totalLabel: { fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '4px' },
  totalValue: { fontSize: '24px', fontWeight: '700', color: 'var(--color-text)', lineHeight: 1.2 },
  totalCount: { fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' },
};
